// Typed-intake client (typed help/intake framework, commit 2).
//
// Mirrors feedbackMarkup/feedbackApi.js: one place that knows the intake
// endpoints, the UUID Idempotency-Key contract, and the envelope shape, so no
// form component ever holds a raw fetch().
//
// Two server contracts this module exists to honor:
//   - Create and submit REQUIRE a UUID Idempotency-Key. A retry after an
//     ambiguous failure must reuse the SAME key so the server replays the
//     original result instead of creating a second intake or a second governed
//     record. Callers that own a retry hold the key; callers that do not still
//     get a fresh one.
//   - Every aggregate mutation carries `expectedRevision`, and a stale one comes
//     back as HTTP 409 with `error.details.currentRevision`. Callers must keep
//     the reporter's typed work and refresh, never discard it.

import { api } from '../../lib/apiClient.js';

const BASE = '/feedback/intakes';

export function newIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  // Browser crypto is present in every supported PRIME environment; this keeps
  // UUID shape for older local test runners only.
  return '00000000-0000-4000-8000-'.concat(
    Math.random().toString(16).slice(2).padEnd(12, '0').slice(0, 12),
  );
}

function intakePath(intakeId, suffix = '') {
  return `${BASE}/${encodeURIComponent(intakeId)}${suffix}`;
}

export async function createSupportTicketIntake(payload, { idempotencyKey } = {}) {
  return api.post(
    BASE,
    { intakeType: 'support_ticket', ...payload },
    {
      headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() },
    },
  );
}

export async function getSupportTicketIntake(intakeId) {
  return api.get(intakePath(intakeId));
}

export async function patchSupportTicketIntake(intakeId, payload) {
  return api.patch(intakePath(intakeId), payload);
}

export async function prepareSupportTicketIntake(intakeId, expectedRevision, previewBinding) {
  return api.post(intakePath(intakeId, '/prepare'), { expectedRevision, previewBinding });
}

export async function submitSupportTicketIntake(
  intakeId,
  expectedRevision,
  { idempotencyKey, previewBinding } = {},
) {
  return api.post(
    intakePath(intakeId, '/submit'),
    { expectedRevision, previewBinding },
    { headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() } },
  );
}

export async function cancelSupportTicketIntake(intakeId, expectedRevision, reason = null) {
  return api.post(intakePath(intakeId, '/cancel'), { expectedRevision, reason });
}

// The exact artifact the service desk would receive. Recipients in the response
// are DISPLAY ONLY — they are server configuration, and no write in this module
// echoes them back.
export async function getGscTicketPreview(intakeId) {
  return api.get(intakePath(intakeId, '/gsc-preview'));
}

export async function acknowledgeSupportTicketCui(
  intakeId,
  expectedRevision,
  acknowledgementVersion,
  { idempotencyKey } = {},
) {
  return api.post(
    intakePath(intakeId, '/gsc-acknowledgement'),
    { expectedRevision, acknowledgementVersion },
    { headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() } },
  );
}

export async function getSupportTicketDelivery(intakeId) {
  return api.get(intakePath(intakeId, '/dispatch'));
}

// The .eml is a downloadable message, not JSON, so it bypasses the envelope
// helper. Same-origin `credentials: 'include'` matches the api client.
export function gscTicketEmlUrl(intakeId) {
  const base = import.meta.env?.VITE_API_BASE || '/api';
  return `${base}${intakePath(intakeId, '/gsc-preview.eml')}`;
}

export async function fetchGscTicketEml(intakeId) {
  const response = await fetch(gscTicketEmlUrl(intakeId), { credentials: 'include' });
  if (!response.ok) {
    const error = new Error('The ticket file could not be prepared.');
    error.status = response.status;
    throw error;
  }
  return response.text();
}

export async function listSupportTicketIntakes({ status = null, limit = 50, offset = 0 } = {}) {
  return api.get(BASE, { query: { type: 'support_ticket', status, limit, offset } });
}

// Every typed intake the signed-in reporter owns, whatever its type (commit 4).
// One request rather than one per vertical: the server already returns the type on
// each row, and merging two paginated sources in the browser is exactly the mistake
// the architecture review flagged (§8.16).
// `type` narrows the unified My Feedback list to one vertical (commit 6). It is
// applied SERVER SIDE so `page.total` and `page.hasMore` describe the filtered
// set — narrowing a page in the browser produces a "Showing 3 so far" that never
// reaches the end. The unified STATE filter stays client-side on purpose: it is
// derived from the intake status plus the linked record's status, and the server
// list has no single column to filter it on.
export async function listMyIntakes({ type = null, status = null, limit = 50, offset = 0 } = {}) {
  return api.get(BASE, { query: { type, status, limit, offset } });
}
